import MenuSection from '../../../types/MenuSection';
import MenuSpec from '../../../types/MenuSpec';

interface AliasMap
{
    [ name: string ]: Array<string>;
}

export default class SectionUtils
{
    static loadedImages = {};

    static findImage( sectionId: string, isActive: boolean ): string | undefined
    {

        if ( !SectionUtils.hasImage( sectionId ) )
        {
            return undefined;
        }

        const allImages = SectionUtils.loadedImages[ sectionId ];
        const modifier = isActive ? 'active' : 'inactive';
        return allImages[ modifier ];
    }

    static hasImage( sectionId: string ): boolean
    {
        return ( sectionId !== "" ) && SectionUtils.loadedImages.hasOwnProperty( sectionId );
    }

    static isActive( sectionSpec: MenuSection, activeSectionSpec: MenuSection ): boolean
    {
        if ( !activeSectionSpec || !activeSectionSpec.id )
        {
            return false;
        }

        if ( sectionSpec.id === activeSectionSpec.id )
        {
            return true;
        }

        if ( sectionSpec.children && sectionSpec.children.length )
        {
            return !!sectionSpec.children.find( ( childSpec ) => ( SectionUtils.isActive( childSpec, activeSectionSpec ) ) );
        }

        return false;
    }

    static findOpenSectionId( leftSideMenu: MenuSpec | null, activeSection: MenuSection | null ): string | null
    {
        if ( !leftSideMenu )
        {
            return null;
        }

        if ( !activeSection )
        {
            // Menu does not have an active section, so just open the top one
            if ( leftSideMenu.menu && leftSideMenu.menu.length > 0 )
            {
                return leftSideMenu.menu[ 0 ].id;
            }

            return null; // Menu is completely empty
        }

        const children: Array<MenuSection> = leftSideMenu.menu;
        const activeChild = children && children.length > 0 && children.find( ( childSpec ) =>
        {
            return SectionUtils.isActive( childSpec, activeSection );
        } );

        if ( activeChild )
        {
            return activeChild.id;
        }

        return null;
    }

    static loadTwoStateImage( sectionId: string, imageName: string ): void
    {
        SectionUtils.loadedImages[ sectionId ] = {
            active: require( '../images/menu/active/' + imageName ),
            inactive: require( '../images/menu/inactive/' + imageName )
        };
    }

    static loadOneStateImage( sectionId: string, imageName: string ): void
    {
        SectionUtils.loadedImages[ sectionId ] = {
            active: require( '../images/menu/' + imageName )
        };
    }

    static loadImages(): void
    {
        SectionUtils.loadTwoStateImage( '_home', 'overview.svg' );
        SectionUtils.loadTwoStateImage( '_appdiscovery', 'appdiscovery.svg' );
        SectionUtils.loadTwoStateImage( '_max', 'max.svg' );
        SectionUtils.loadTwoStateImage( '_safedk', 'safedk.png' );
        SectionUtils.loadTwoStateImage( '_compass', 'compass.svg' );
        SectionUtils.loadTwoStateImage( '_internal', 'internal.svg' );
        SectionUtils.loadTwoStateImage( '_account', 'account.svg' );
        SectionUtils.loadTwoStateImage( '_resources', 'resources.svg' );
        SectionUtils.loadTwoStateImage( '_ab_tests', 'abtest.svg' );
        SectionUtils.loadTwoStateImage( '_campaigns', 'web_campaigns.svg' );
        SectionUtils.loadTwoStateImage( '_documentation', 'resources.svg' );
        SectionUtils.loadTwoStateImage( '_analytics', 'internal.svg' );

        SectionUtils.loadOneStateImage( '_docs', 'docs.svg' );
        SectionUtils.loadOneStateImage( '_debug', 'debug.svg' );
        SectionUtils.loadOneStateImage( '_help', 'help.svg' );
        SectionUtils.loadOneStateImage( '__account___account_general_basic_info', 'menu_account.svg' );
        SectionUtils.loadOneStateImage( '1_log_out', 'menu_logout.svg' );

        SectionUtils.loadOneStateImage( '1_superuse', 'menu_superuse.svg' );
        SectionUtils.loadOneStateImage( '1_unsuperuse', 'menu_unsuperuse.svg' );

        SectionUtils.loadOneStateImage( '1_superbulb', 'menu_superbulb.svg' );
        SectionUtils.loadOneStateImage( '1_supercake', 'menu_supercake.svg' );
        SectionUtils.loadOneStateImage( '1_supercoffee', 'menu_supercoffee.svg' );
        SectionUtils.loadOneStateImage( '1_superzzz', 'menu_superzzz.svg' );
    }

    static sortOrderComparator( a: MenuSection, b: MenuSection ): number
    {
        return a.sortOrder - b.sortOrder;
    }

    static getActiveMenuSectionFromUrl( menu: Array<MenuSection> | null, url: string ): MenuSection | null
    {
        const menuLeaves: Array<MenuSection> = SectionUtils.getMenuLeaves( menu );
        const normalizedUrl: string = SectionUtils.normalizeUrl( url );

        let activeSectionDistance: number = Number.MAX_SAFE_INTEGER;
        let activeSection: MenuSection | null = null;

        menuLeaves.sort( SectionUtils.sortOrderComparator );

        for ( const menuSection of menuLeaves )
        {
            const distance: number = SectionUtils.getUrlDistance( normalizedUrl, menuSection.sectionUrl );

            if ( distance < activeSectionDistance )
            {
                activeSection = menuSection;
                activeSectionDistance = distance;
            }
        }

        if ( !activeSection )
        {
            if ( menuLeaves.length > 0 )
            {
                return menuLeaves[ 0 ];
            }

            return null;
        }

        return activeSection;
    }

    static getMenuLeaves( menu: Array<MenuSection> | null ): Array<MenuSection>
    {
        const menuLeaves: Array<MenuSection> = [];

        if ( menu && menu.length > 0 )
        {
            const menuStack: Array<MenuSection> = [ ...menu ];

            while ( menuStack.length > 0 )
            {
                const menuSection: MenuSection = ( menuStack.pop() as MenuSection );

                if ( menuSection.children.length > 0 )
                {
                    Array.prototype.push.apply( menuStack, menuSection.children );
                }
                else if ( menuSection.sectionId !== '' )
                {
                    menuLeaves.push( menuSection );
                }
            }
        }

        return menuLeaves;
    }

    static getUrlDistance( url: string, targetUrl: string ): number
    {
        if ( url === targetUrl )
        {
            return 0;
        }

        if ( url.indexOf( targetUrl ) === 0 )
        {
            return 10;
        }

        const normalizedTargetUrl: string = targetUrl.replace( '#', '/' );

        if ( url.indexOf( normalizedTargetUrl ) === 0 )
        {
            return 20;
        }

        // Now try fuzzy matching
        const urlComponents: Array<string> = url.split( '/' );
        const targetUrlComponents: Array<string> = normalizedTargetUrl.split( '/' );

        if ( urlComponents.length > 1 && targetUrlComponents.length > 1 )
        {
            const urlController: string = urlComponents[ 1 ];
            const targetUrlController: string = targetUrlComponents[ 1 ];

            if ( urlComponents.length > 2 && targetUrlComponents.length > 2 )
            {
                const urlAction: string = urlComponents[ 2 ];
                const targetUrlAction: string = targetUrlComponents[ 2 ];

                if ( urlAction === targetUrlAction )
                {
                    return 50;
                }
            }

            if ( urlController === targetUrlController )
            {
                return 100;
            }

            //
            // PLEASE NOTE: This section below contains various hacks to account for
            // random sections of AppLovin Ads-UI
            //
            const controllerAliasesMap: AliasMap =
                {
                    'accounts': ['account'],
                    'creativeSet': [ 'adminCampaigns', 'promote', 'publishingPromote', 'developerPromote' ],
                    'webCreativeSet': [ 'adminCampaigns', 'webCampaigns' ],
                    'promote': [ 'promote#userAcquisition' ],
                    'directSoldCreativeSet': ['directSold'],
                    'users': ['internal'],
                };

            const actionAliasesMap: AliasMap =
                {
                    'createClones': ['userAcquisition'],
                    'create': ['userAcquisition'],
                    'edit_users': ['users'],
                    'markFraud': ['users'],
                    'superUsePage': ['users'],
                    '_new': ['users'],
                };

            if ( controllerAliasesMap.hasOwnProperty( urlController ) )
            {
                if ( controllerAliasesMap[ urlController ].includes( targetUrlController ) )
                {
                    if ( urlComponents.length > 2 && targetUrlComponents.length > 2 )
                    {
                        const urlAction: string = urlComponents[ 2 ];
                        const targetUrlAction: string = targetUrlComponents[ 2 ];

                        if ( actionAliasesMap[ urlAction ] && actionAliasesMap[ urlAction ].includes( targetUrlAction ) )
                        {
                            return 50;
                        }
                    }

                    return 1000;
                }
            }
        }

        return Number.MAX_SAFE_INTEGER;
    }

    static removeWrapperPrefix( url: string ): string
    {
        if ( url.indexOf( '/o/' ) === 0 )
        {
            url = url.substring( 2 );
        }
        else if ( url === '/o' )
        {
            url = '/';
        }

        return url;
    }

    static normalizeUrl( url: string ): string
    {
        url = SectionUtils.removeWrapperPrefix( url );

        const paramsPos: number = url.indexOf( '?' );

        if ( paramsPos > -1 )
        {
            url = url.substring( 0, paramsPos );
        }

        return url;
    }
}

SectionUtils.loadImages();
